import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Col, Collapse, Form, Row } from "react-bootstrap";
import FontSelector from "./font-selector";
import { useTabRoute } from "../../../hooks/useTabRoute";
import TextArea from "../../reusable-form-fields/text-area";
import TextField from "../../reusable-form-fields/text-field";
import FileInput from "../../reusable-form-fields/input-file";
import SelectField from "../../reusable-form-fields/select-field";
import { CustomCard, FieldSet, Legend } from "../../common-styling";
import FavIconInput from "../../reusable-form-fields/input-favicon";
import { CUSTOMER_WEBSITE_ROUTE, WEBSITE_BASE_TAB_ROUTE } from "../../strings";
import { getCustomerBaseWebsiteFavIcon } from "../../../services/api-declaration";
import { checkPhoneNumFieldFormatOnKeyDown } from "../../../services/common-service";
import ColorPickerWithTextField from "../../reusable-form-fields/color-picker-with-text-field";
import { useTranslation } from "react-i18next";

const HeaderSection = ({
  logoTypes,
  parseResourceSrc,
  parsePreviewResourceSrc,
  sections,
  isFormChanged,
  customerData
}) => {
  const { t } = useTranslation();
  const { tabRoute } = useTabRoute();
  const {
    formState: { errors },
    control,
    watch,
    register,
    getValues,
    setValue
  } = useFormContext();

  const navbarLogoType = watch("navbar.logo.type");
  const navbarLogoImage = watch("navbar.logo.image");
  const favIconImage = watch("favIcon");
  const NavbarBGColorDisplay = watch("navbar.bgColorDisplay");
  const secondPhoneSwitchEnabled = watch("navbar.secondPhone.switchEnabled");
  const secondAddressSwitchEnabled = watch(
    "navbar.secondAddress.switchEnabled"
  );
  const navbarBgColorPickerRef = useRef();
  const navbarLogoColorPickerRef = useRef();
  const navbarLinksColorPickerRef = useRef();
  const phoneColorPickerRef = useRef();
  const addressColorPickerRef = useRef();
  const isBaseTab = tabRoute === WEBSITE_BASE_TAB_ROUTE;

  useEffect(() => {
    const getBaseWebsiteFavIcon = async () => {
      try {
        if (isBaseTab || !customerData?._id) {
          return;
        }
        const response = await getCustomerBaseWebsiteFavIcon({
          customerId: customerData._id
        });
        const baseFavIcon = response?.data?.data?.data?.favIcon;
        if (!favIconImage && baseFavIcon) {
          setValue("favIcon", baseFavIcon);
        }
      } catch (error) {
        console.log("Error in fetching base website fav icon ", error);
      }
    };
    getBaseWebsiteFavIcon();
  }, [isBaseTab, customerData?._id, favIconImage, setValue]);

  return (
    <CustomCard className="mb-3 py-1">
      <FieldSet>
        <Row>
          <Col md={12}>
            <Legend>
              <h4 className="mb-0 fw-bold">{t("customerWebsite.header")}</h4>
            </Legend>
          </Col>
          <Col md={3} className="mt-1">
            <ColorPickerWithTextField
              label={t("customerWebsite.BGColor")}
              fieldName="navbar.bgColor"
              fieldError={errors?.navbar?.bgColor}
              fieldRef={navbarBgColorPickerRef}
              fieldVal={watch("navbar.bgColor")}
              displayFieldWatcher={NavbarBGColorDisplay}
              fieldWatcherName="navbar.bgColorDisplay"
              isDisabled={Boolean(!NavbarBGColorDisplay)}
              setDisplay
              isInGroup
              noSpacing
              hideErrMsg
              hideButton
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={12}>
            <Legend>
              <h6 className="mb-0 fw-bold mb-3">
                {t("customerWebsite.logo.heading")}
              </h6>
            </Legend>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <Form.Group className="mb-4" controlId="land">
              <Form.Label>{t("customerWebsite.logo.logoType")}</Form.Label>
              <SelectField
                control={control}
                name="navbar.logo.type"
                options={logoTypes}
                getValues={getValues}
                error={errors?.navbar?.logo?.type?.name?.message}
              />
            </Form.Group>
          </Col>

          {navbarLogoType?._id === logoTypes[0]._id ? (
            <>
              <Col sm={12} md={4}>
                <TextField
                  label={t("customerWebsite.logo.logoText")}
                  placeholder="Logo Text"
                  type="text"
                  name="navbar.logo.text"
                  errors={errors?.navbar?.logo?.text}
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={4}>
                <ColorPickerWithTextField
                  label={t("customerWebsite.logo.logoColor")}
                  placeholder="#000000"
                  fieldName="navbar.logo.color"
                  fieldError={errors?.navbar?.logo?.color}
                  fieldRef={navbarLogoColorPickerRef}
                  fieldVal={watch("navbar.logo.color")}
                  defaultColorValue="#000000"
                  isInGroup
                  noSpacing
                  hideErrMsg
                  hideButton
                  isFormChanged={isFormChanged}
                />
              </Col>
            </>
          ) : navbarLogoType?._id === logoTypes[1]._id ? (
            <>
              <Col xs={12} md={4}>
                <FileInput
                  label={t("customerWebsite.logo.logoImage")}
                  name="navbar.logo.image"
                  // resourceId={resourceId}
                  parentFolderName={"customer"}
                  url={parseResourceSrc(navbarLogoImage)}
                  urlForPreview={parsePreviewResourceSrc(navbarLogoImage)}
                  asset={navbarLogoImage?.asset}
                  error={errors?.navbar?.logo?.image}
                  isFormChanged={isFormChanged}
                  // selectedCategory={getValues("category")}
                  // tagName="backgroundimage"
                />
              </Col>
              <Col xs={12} md={4}>
                <TextField
                  label={t("customerWebsite.logo.logoScale")}
                  placeholder="100%"
                  type="number"
                  name="navbar.logo.scale"
                  register={register}
                  blurOnScroll
                  noSpacing
                />
              </Col>
            </>
          ) : null}
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={6}>
            <FavIconInput
              label={"Fav Icon"}
              suggestionText={
                !isBaseTab
                  ? `(${t("customerWebsite.favIconSuggestionText")})`
                  : ""
              }
              name="favIcon"
              parentFolderName={CUSTOMER_WEBSITE_ROUTE}
              resourceId={customerData?._id}
              assetData={favIconImage}
              assetKey={favIconImage?.key}
              error={errors?.favIcon}
              isFormChanged={isFormChanged}
              isUploadAllowed={isBaseTab}
              isRemoveAllowed={isBaseTab}
              removeBtnDisabled={!favIconImage}
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between">
              <Legend>
                <h6 className="mb-0 fw-bold mb-1">
                  {t("customerWebsite.navLinks")}
                </h6>
              </Legend>
              <FontSelector
                formField="navbar.links"
                isFormChanged={isFormChanged}
                asFlex
              />
            </div>
          </Col>
          <Col md={3}>
            <div>
              <ColorPickerWithTextField
                label={t("customerWebsite.linksTextColor")}
                placeholder="#000000"
                fieldName="navbar.links.color"
                fieldError={errors?.navbar?.links?.color}
                fieldRef={navbarLinksColorPickerRef}
                fieldVal={watch("navbar.links.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                hideButton
                isFormChanged={isFormChanged}
              />
            </div>
          </Col>
          <Col md={12}>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="navbar.links.links[0].text"
                  errors={
                    errors?.navbar?.links?.links &&
                    errors?.navbar?.links?.links[0]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="navbar.links.links[0].link"
                    options={sections}
                    getValues={getValues}
                    error={errors?.navbar?.links?.links[0]?.link?.name?.message}
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="navbar.links.links[1].text"
                  errors={
                    errors?.navbar?.links?.links &&
                    errors?.navbar?.links?.links[1]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="navbar.links.links[1].link"
                    options={sections}
                    getValues={getValues}
                    error={errors?.navbar?.links?.links[1]?.link?.name?.message}
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="navbar.links.links[2].text"
                  errors={
                    errors?.navbar?.links?.links &&
                    errors?.navbar?.links?.links[2]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="navbar.links.links[2].link"
                    options={sections}
                    getValues={getValues}
                    error={errors?.navbar?.links?.links[2]?.link?.name?.message}
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="navbar.links.links[3].text"
                  errors={
                    errors?.navbar?.links?.links &&
                    errors?.navbar?.links?.links[3]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="navbar.links.links[3].link"
                    options={sections}
                    getValues={getValues}
                    error={errors?.navbar?.links?.links[3]?.link?.name?.message}
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between">
              <Legend>
                <h6 className="mb-0 fw-bold mb-3">
                  {t("customerWebsite.contact")}
                </h6>
              </Legend>
              <FontSelector
                formField="navbar.contact"
                isFormChanged={isFormChanged}
                asFlex
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.phone")}
              placeholder={t("customerWebsite.phone")}
              type="text"
              name="navbar.phone.text"
              errors={errors?.navbar?.phone?.text}
              register={register}
              defaultValue={customerData?.phone}
              handleKeyDown={(e) => checkPhoneNumFieldFormatOnKeyDown(e)}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              label={t("customerWebsite.phoneIconColor")}
              placeholder="#000000"
              fieldName="navbar.phone.color"
              fieldError={errors?.navbar?.phone?.color}
              fieldRef={phoneColorPickerRef}
              fieldVal={watch("navbar.phone.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              hideButton
              isFormChanged={isFormChanged}
            />
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.address")}
              placeholder={t("customerWebsite.address")}
              type="text"
              name="navbar.address.text"
              errors={errors?.navbar?.address?.text}
              defaultValue={
                customerData
                  ? `${customerData?.address || ""} ${
                      customerData?.zipCode || ""
                    } ${customerData?.city?.name || ""}`.trim()
                  : ""
              }
              register={register}
              multiline
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              label={t("customerWebsite.addressIconColor")}
              placeholder="#000000"
              fieldName="navbar.address.color"
              fieldError={errors?.navbar?.address?.color}
              fieldRef={addressColorPickerRef}
              fieldVal={watch("navbar.address.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              hideButton
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={12} md={6}>
            <div>
              <Form.Check
                role="button"
                type="switch"
                className="fw-bold"
                label={`${t("common.enable")} ${t("customerWebsite.phone")} 2`}
                {...register("navbar.secondPhone.switchEnabled")}
              />
            </div>
            <Collapse in={secondPhoneSwitchEnabled}>
              <div>
                <TextField
                  label={`${t("customerWebsite.phone")} 2 (optional)`}
                  placeholder={`${t("customerWebsite.phone")} 2`}
                  type="text"
                  name="navbar.secondPhone.text"
                  errors={errors?.navbar?.secondPhone?.text}
                  register={register}
                  handleKeyDown={(e) => checkPhoneNumFieldFormatOnKeyDown(e)}
                  noSpacing
                />
              </div>
            </Collapse>
          </Col>
          <Col sm={12} md={6}>
            <div>
              <Form.Check
                role="button"
                type="switch"
                className="fw-bold"
                label={`${t("common.enable")} ${t(
                  "customerWebsite.address"
                )} 2`}
                {...register("navbar.secondAddress.switchEnabled")}
              />
            </div>
            <Collapse in={secondAddressSwitchEnabled}>
              <div>
                <TextField
                  label={`${t("customerWebsite.address")} 2 (optional)`}
                  placeholder={`${t("customerWebsite.address")} 2`}
                  type="text"
                  name="navbar.secondAddress.text"
                  errors={errors?.navbar?.secondAddress?.text}
                  register={register}
                  multiline
                  noSpacing
                />
              </div>
            </Collapse>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={12}>
            <Legend>
              <h6 className="mb-0 fw-bold mb-3">
                {t("customerWebsite.script.label")}
              </h6>
            </Legend>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <TextArea
              register={register}
              name="navbar.script.head"
              label={t("customerWebsite.script.head")}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextArea
              register={register}
              name="navbar.script.body"
              label={t("customerWebsite.script.body")}
            />
          </Col>
        </Row>
      </FieldSet>
    </CustomCard>
  );
};

export default HeaderSection;
